import React, {useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import DataTable from 'react-data-table-component';
import {getReport, ReportFilterValues, ReportPayload} from '@app/services/reports';
import {getAllActiveFranchise} from '@app/services/franchise';
import KpiStrip from './components/KpiStrip';
import ReportFilters, {FranchiseOption} from './components/ReportFilters';
import {ChartCard, ReportMultiSeriesChart, CHART_COLOURS} from './components/ReportChart';
import useReportExport from './hooks/useReportExport';
import {reportTableStyles} from './components/tableStyles';
import {findReportBySlug} from './reportCatalogue';

/**
 * LEAD2 — Source Effectiveness.
 *
 * Volume and conversion are close to opposites here: Website supplies most of
 * the leads and converts worst, while the small channels convert best. Showing
 * either alone misleads, so the chart carries both — volume as bars, conversion
 * as a line on a second axis.
 *
 * Rates off a handful of leads are noise. The API flags those (reliable:false)
 * and the table marks them rather than hiding them, so nobody re-allocates
 * budget on an 83% that came from twelve leads.
 */

const SLUG = 'lead-sources';
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length: 5}, (_, i) => CURRENT_YEAR - i);

const LeadSources = () => {
  const definition = findReportBySlug(SLUG);

  const [payload, setPayload] = useState<ReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [franchises, setFranchises] = useState<FranchiseOption[]>([]);

  const [filters, setFilters] = useState<ReportFilterValues>({
    mode: 'year',
    year: CURRENT_YEAR,
    period: 'full',
    source: 'found_via',
    page: 1,
    limit: 25
  });
  const [applied, setApplied] = useState<ReportFilterValues>(filters);

  const {exportToExcel, exporting} = useReportExport('Lead Sources');

  const load = useCallback(async (active: ReportFilterValues) => {
    setLoading(true);
    setError(null);
    const response = await getReport(SLUG, active);
    if (response && response.success) {
      setPayload(response);
    } else {
      setPayload(null);
      setError(response?.message || 'The report could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(() => {
    (async () => {
      const response = await getAllActiveFranchise();
      if (response?.success === true && Array.isArray(response.collections)) {
        setFranchises(
          response.collections.map((f: any) => ({value: f.id, label: f.name}))
        );
      }
    })();
  }, []);

  const collections = payload?.collections;
  const bySource = collections?.series?.by_source || [];
  const detail = collections?.detail;

  const columns = [
    {name: 'Source', selector: (row: any) => row.source || '---', wrap: true},
    {name: 'Leads', width: '100px', right: true, selector: (row: any) => row.leads},
    {name: 'Enrolled', width: '110px', right: true, selector: (row: any) => row.enrolled},
    {
      name: 'Conversion',
      width: '130px',
      right: true,
      cell: (row: any) => (
        <span className={row.reliability === 'Reliable' ? '' : 'text-muted'}>
          {row.conversion}%
        </span>
      )
    },
    {
      name: 'Reliability',
      width: '180px',
      cell: (row: any) => (
        <span
          className={`badge badge-${row.reliability === 'Reliable' ? 'success' : 'secondary'}`}
          title={
            row.reliability === 'Reliable'
              ? 'Enough leads for the rate to mean something'
              : 'Too few leads — treat this rate as noise'
          }>
          {row.reliability}
        </span>
      )
    }
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-7">
              <h1>{definition?.name || 'Source Effectiveness'}</h1>
              <p className="text-muted mb-0">{definition?.question}</p>
            </div>
            <div className="col-sm-5">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/reports">Reports</Link></li>
                <li className="breadcrumb-item active">Source Effectiveness</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <ReportFilters
            values={filters}
            onChange={setFilters}
            onApply={() => setApplied({...filters})}
            onReset={(defaults) => {
              setFilters(defaults);
              setApplied(defaults);
            }}
            onExport={() => exportToExcel(payload, SLUG, applied)}
            exporting={exporting}
            franchises={franchises}
            years={YEARS}
            intervalLabel={collections?.filters_applied?.interval_label}
            show={{franchise: true, subject: true, leadStudent: true, foundVia: true, gotInTouch: true}}
            disabled={loading}
          />

          {error && (
            <div className="alert alert-warning">
              <i className="fas fa-exclamation-triangle mr-2" />
              {error}
            </div>
          )}

          <KpiStrip kpis={collections?.kpis || []} loading={loading} />

          <div className="row">
            <div className="col-12">
              <ChartCard
                title="Volume against conversion"
                subtitle="Bars are how many leads a source produced; the line is how well it converted. They usually move in opposite directions."
                loading={loading}
                isEmpty={!bySource.length}
                height={380}>
                <ReportMultiSeriesChart
                  data={bySource}
                  xKey="source"
                  currency={false}
                  height={340}
                  series={[
                    {key: 'leads', name: 'Leads', type: 'bar', colour: CHART_COLOURS[1]},
                    {key: 'conversion', name: 'Conversion', type: 'line', axis: 'right', format: 'percent', colour: CHART_COLOURS[2]}
                  ]}
                />
              </ChartCard>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title text-capitalize">By source</h3>
            </div>
            <div className="card-body p-0">
              <DataTable
                columns={columns}
                customStyles={reportTableStyles}
                data={detail?.rows || []}
                progressPending={loading}
                noDataComponent={
                  <div className="text-muted py-4">No leads match these filters.</div>
                }
                highlightOnHover
                dense
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadSources;
